import { type Query, useIsFetching } from "@tanstack/react-query";
import {
  createContext,
  type FC,
  type PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { AuthContext } from "@/providers/auth-provider";
import { cx } from "@/utils/cx";

const CREAM_IN_MS = 450;
const VIDEO_START_MS = 270;
const LOGO_HOLD_MS = 2570;
const REDUCED_HOLD_MS = 750;
const DATA_WAIT_CAP_MS = 4000;
const LOGO_OUT_MS = 500;
const REVEAL_MS = 800;

type Phase = "idle" | "cover" | "logo-out" | "reveal";

/**
 * A query a mounted component is still waiting on for its first data, so its part of the page would
 * be revealed empty. A refetch of data already on screen, polling included, changes nothing visible.
 */
const isFirstLoadOnScreen = (query: Query) => query.state.status === "pending" && query.getObserversCount() > 0;

/** `start` resolves once the cream fully covers the screen, so whatever renders next stays hidden. */
const LoginTransitionContext = createContext<{ start: () => Promise<void> }>({ start: async () => {} });

export const useLoginTransition = () => useContext(LoginTransitionContext);

/**
 * Covers the app with the animated Unbox mark on cream after a successful login, then reveals what
 * the router rendered underneath. It sits outside the router because signing in briefly replaces the
 * whole router with a loading state, which would unmount anything mounted inside it.
 */
export const LoginTransitionProvider: FC<PropsWithChildren> = ({ children }) => {
  const { isAuthenticated } = useContext(AuthContext);
  const isLoadingVisible = useIsFetching({ predicate: isFirstLoadOnScreen });
  const videoRef = useRef<HTMLVideoElement>(null);
  const timers = useRef<ReturnType<typeof setTimeout>[]>([]);
  const [phase, setPhase] = useState<Phase>("idle");
  const [reducedMotion, setReducedMotion] = useState(false);
  const [holdDone, setHoldDone] = useState(false);
  const [waitCapped, setWaitCapped] = useState(false);

  const later = useCallback((fn: () => void, ms: number) => {
    timers.current.push(setTimeout(fn, ms));
  }, []);

  useEffect(() => () => timers.current.forEach(clearTimeout), []);

  const start = useCallback(async () => {
    if (phase !== "idle") return;
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const hold = reduced ? REDUCED_HOLD_MS : LOGO_HOLD_MS;
    setReducedMotion(reduced);
    setHoldDone(false);
    setWaitCapped(false);
    setPhase("cover");
    if (!reduced) {
      later(() => {
        const video = videoRef.current;
        if (!video) return;
        video.currentTime = 0;
        video.play().catch(() => {});
      }, VIDEO_START_MS);
    }
    later(() => setHoldDone(true), hold);
    later(() => setWaitCapped(true), hold + DATA_WAIT_CAP_MS);
    await new Promise<void>((resolve) => later(resolve, CREAM_IN_MS));
  }, [phase, later]);

  const homeReady = isAuthenticated === true && isLoadingVisible === 0;

  useEffect(() => {
    if (phase !== "cover" || !holdDone || !(homeReady || waitCapped)) return;
    if (reducedMotion) {
      setPhase("reveal");
      later(() => setPhase("idle"), REVEAL_MS);
      return;
    }
    setPhase("logo-out");
    later(() => setPhase("reveal"), LOGO_OUT_MS);
    later(() => {
      setPhase("idle");
      videoRef.current?.pause();
    }, LOGO_OUT_MS + REVEAL_MS);
  }, [phase, holdDone, homeReady, waitCapped, reducedMotion, later]);

  const scaled = !reducedMotion && (phase === "logo-out" || phase === "reveal");
  const showOverlay = phase !== "idle" || isAuthenticated === false;

  return (
    <LoginTransitionContext.Provider value={{ start }}>
      <div className={cx(scaled && "h-dvh w-full overflow-hidden")}>
        <div
          className={cx(
            scaled && "h-full ease-[cubic-bezier(0.33,0,0.2,1)]",
            phase === "logo-out" && "scale-[1.012]",
            phase === "reveal" && scaled && "scale-100 transition-transform duration-[800ms]",
          )}
        >
          {children}
        </div>
      </div>
      {showOverlay && (
        <div
          aria-hidden
          data-testid="login-transition"
          className={cx(
            "pointer-events-none fixed inset-0 z-[10000] grid place-items-center bg-unbox-cream-50 opacity-0 transition-opacity ease-[cubic-bezier(0.33,0,0.2,1)]",
            (phase === "cover" || phase === "logo-out") && "opacity-100 duration-[450ms]",
            phase === "reveal" && "duration-[800ms]",
          )}
        >
          <video
            ref={videoRef}
            src="/brand/unbox-logoword-indigo.mp4"
            muted
            playsInline
            preload="auto"
            className={cx(
              "aspect-video w-[calc(20vw/0.617)] max-w-none mix-blend-multiply transition-opacity duration-500 ease-[cubic-bezier(0.33,0,0.2,1)]",
              (reducedMotion || phase === "logo-out" || phase === "reveal") && "opacity-0",
            )}
          />
        </div>
      )}
    </LoginTransitionContext.Provider>
  );
};
